import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { getFirstAndLastEmiDates,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import { useAdminAuth } from "../../auth/AdminAuthProvider";

import DashboardHeader from "../../../components/layout/DashboardHeader";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

function GplClientProspectReport() {
  const { adminUser } = useAdminAuth();

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const OpenSidebar = () => setOpenSidebarToggle(!openSidebarToggle);

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(`${BASE_URL}/api/getGplclientprospectreport`, {
          params: { id },
        });
        if (response.data) setReportData(response.data);
      } catch (error) {
        console.error("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  const branchOptions = [...new Set(filteredData.map((d) => d?.branchName))]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(filteredData.map((d) => d?.username))]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  useEffect(() => {
    let filtered = reportData;

    if (fromDate && toDate) {
      filtered = filtered
        .filter((item) => {
          const updatedAt = new Date(item.createdAt);
          const fromDateObj = new Date(fromDate);
          const toDateObj = new Date(toDate);
          fromDateObj.setHours(0, 0, 0, 0);
          toDateObj.setHours(23, 59, 59, 999);
          return updatedAt >= fromDateObj && updatedAt <= toDateObj;
        });
    }

    filtered = filtered
      .filter((item) => {
        if (selectedBranchFilter?.value) return item.branchName === selectedBranchFilter.value;
        return true;
      })
      .filter((item) => {
        if (selectedEmployeeFilter?.value) return item.username === selectedEmployeeFilter.value;
        return true;
      });

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [fromDate, toDate, reportData, selectedBranchFilter, selectedEmployeeFilter, searchTerm]);

  const columns = [
    { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) => {
        const parts = [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
          data.permanentPincode
        ].filter(part => part && String(part).trim() !== '');
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Sub Category",
      render: (data) => data?.businessDetails?.natureOfBusiness ?? "N/A",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data?.proposedLoanDetails?.rateOfInterest ?? "N/A",
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" && data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        if (
          !data?.branchManagerStatusUpdatedAt ||
          !data?.proposedLoanDetails?.tenureInMonths ||
          !data?.emiDateByBranchManager
        ) {
          return "";
        }
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data?.eligibleLoanAmountByBm ??
        data?.proposedLoanDetails?.loanAmount ??
        "N/A",
    },
    {
      label: "Role",
      render: (data) => {
        const isGPL = data.loanType === "GP Loan";
        const fmS = data.fieldManagerStatus;
        const bmS = data.branchManagerStatus;
        const coS = data.creditOfficerStatus;
        const misS = data.misStatus;
        const cmS = data.creditManagerStatus;
        const sacS = data.sanctionCommitteeStatus;
        const amS = data.accountManagerStatus;

        // Retrigger/Rejected logic (Highest priority)
        if (fmS === "retrigger") return "Customer Relationship Officer";
        if (["retrigger", "rejected"].includes(bmS)) return "Branch Manager";
        if (!isGPL && ["retrigger", "rejected"].includes(coS)) return "Credit Officer";
        if (["retrigger", "rejected", "retrigger_stage2"].includes(misS)) return "MIS";
        if (["retrigger", "rejected"].includes(cmS)) return "Credit Manager";
        if (!isGPL && ["retrigger", "rejected"].includes(sacS)) return "Sanction Committee";
        if (amS === "rejected") return "Account Manager";

        // Pending logic (Workflow sequence)
        if (fmS === "pending") return "Customer Relationship Officer";
        
        if (fmS === "submitted" && bmS === "pending") return "Branch Manager";
        
        if (!isGPL) {
          // Business Loan Workflow
          if (bmS === "submitted" && coS === "pending") return "Credit Officer";
          if (coS === "submitted" && misS === "pending") return "MIS";
          if (misS === "submitted" && cmS === "pending") return "Credit Manager";
          if (cmS === "submitted" && sacS === "pending") return "Sanction Committee";
          if (sacS === "approved" && amS === "pending") return "Account Manager";
        } else {
          // GP Loan Workflow: CRO -> BM -> MIS -> CM -> BM -> MIS -> AM
          if (bmS === "submitted" && misS === "pending") return "MIS";
          if (misS === "orderPlaced" && cmS === "pending") return "Credit Manager";
          if (cmS === "submitted" && bmS === "submitted") return "Branch Manager (Stage 2)";
          if (bmS === "disbursed" && (misS === "orderPlaced" || misS === "submitted")) return "MIS (Final Recheck)";
          if (misS === "recheckFinally" && amS === "pending") return "Account Manager";
        }

        if (amS === "payment credited" || bmS === "disbursed") return "Disbursed / Completed";

        return "Unknown Role";
      },
    },
    {
      label: "Status",
      render: (data) => {
        const isGPL = data.loanType === "GP Loan";
        const statuses = [
          data.fieldManagerStatus,
          data.branchManagerStatus,
          isGPL ? null : data.creditOfficerStatus,
          data.misStatus,
          data.creditManagerStatus,
          isGPL ? null : data.sanctionCommitteeStatus,
          data.accountManagerStatus,
        ].filter(Boolean);

        if (statuses.some(s => ["retrigger", "retrigger_stage2", "retriggertoeditbankdetails"].includes(s))) {
          return <span style={{ color: "#ffc107", fontWeight: "bold" }}>retrigger</span>;
        }
        if (statuses.some(s => s === "rejected")) {
          return <span style={{ color: "red", fontWeight: "bold" }}>rejected</span>;
        }
        if (data.accountManagerStatus === "payment credited" || (isGPL && data.misStatus === "disbursed") || (!isGPL && data.accountManagerStatus === "payment credited")) {
          return <span style={{ color: "green", fontWeight: "bold" }}>disbursed</span>;
        }
        if (statuses.some(s => s === "pending")) {
          return <span style={{ color: "var(--text-muted)", fontWeight: "bold" }}>pending</span>;
        }

        return <span style={{ fontWeight: "bold" }}>submitted</span>;
      },
    },
    { label: "Aadhar Number", render: (data) => data.aadharNo },
    { label: "Pan/Other Identity", render: (data) => (data.isPanNo === 1 ? data.panNo : data.anotherIdentityId) },
    {
      label: "OnBoard Date",
      render: (data) =>
        new Date(data.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
    },
    {
      label: "Opened Time",
      render: (data) =>
        new Date(data.createdAt).toLocaleTimeString("en-GB"),
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">GPL Client Prospect Report</h1>
            <Row className="w-100" style={{ fontWeight: "500" }}>
              <Col xs={12} lg={4} md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>Search Customer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} lg={2} md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>From Date</Form.Label>
                  <StandardizedDateInput
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6} lg={2} md={6} className="mb-2">
                <Form.Group>
                  <Form.Label>To Date</Form.Label>
                  <StandardizedDateInput
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} lg={4} md={6} className="mb-2">
                <Form.Label>Branch</Form.Label>
                <Select
                  value={selectedBranchFilter}
                  onChange={setSelectedBranchFilter}
                  options={branchOptions}
                  isClearable
                  placeholder="Filter by Branch Name"
                />
              </Col>
              <Col xs={12} lg={4} md={6} className="mb-2">
                <Form.Label>Employee</Form.Label>
                <Select
                  value={selectedEmployeeFilter}
                  onChange={setSelectedEmployeeFilter}
                  options={employeeOptions}
                  isClearable
                  placeholder="Filter by Employee Name"
                />
              </Col>
            </Row>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="GplClientProspectReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplClientProspectReport;